<template>
	<div>
		<v-row>
			<v-col>
				<div class="d-flex align-center">
					<h3 class="title grey--text text--darken-4">Data Warehouse</h3>
					<v-spacer></v-spacer>

					<!-- Dialog -->
					<v-dialog v-model="dialogIndex" width="45%">
						<template v-slot:activator="{ on, attrs }">
							<v-btn small v-bind="attrs" v-on="on" outlined color="primary" elevation="1"
								>Saved searches
								<v-icon right small>mdi-layers-triple</v-icon>
							</v-btn>
						</template>

						<SavedSearch
							@close-dialog-index="dialogIndex = false"
							@edit-saved-search="editSavedSearch"
							@submit-saved-search="submitSavedSearch"
						/>
					</v-dialog>
				</div>
			</v-col>
		</v-row>

		<!-- Tabs -->
		<v-row>
			<v-col>
				<v-card flat>
					<v-tabs v-model="tab" color="grey darken-3">
						<v-tab v-for="(item, index) in items" :key="item.tabId">
							<span class="overline" v-if="item.content.editing || item.content.searching">
								{{ tabName[index] || `New search ${index + 1}` }}
							</span>
							<span class="overline" v-else>New search {{ index + 1 }}</span>

							<v-btn class="ml-4" x-small icon @click="closeTab(index)">
								<v-icon color="grey darken-3">mdi-close</v-icon>
							</v-btn>
						</v-tab>

						<v-btn @click="addTab()" class="mt-2 mx-4" color="grey lighten-3" fab x-small elevation="2">
							<v-icon color="grey darken-3">mdi-plus</v-icon>
						</v-btn>
					</v-tabs>

					<v-divider></v-divider>

					<!-- Tab content -->
					<v-tabs-items v-model="tab">
						<v-tab-item v-for="item in items" :key="item.tabId">
							<!-- Value controls the opened/closed state of content in the expansion-panel. -->
							<v-expansion-panels :value="panel" flat>
								<v-expansion-panel>
									<v-expansion-panel-header :id="item.tabId" :ref="item.tabId">
										<template v-slot:actions>
											<v-icon color="grey darken-3">$expand</v-icon>
										</template>
										<span>
											<span class="mr-2" v-if="countries">
												<strong>Country: </strong>({{ countries }})
											</span>
											<span class="mr-2" v-if="regions"
												><strong>Region: </strong>({{ regions }})</span
											>
											<span class="mr-2" v-if="bricks"
												><strong>Brick: </strong>({{ bricks }})</span
											>

											<span class="mr-2" v-if="atcCodes"
												><strong>ATC Code: </strong>({{ atcCodes }})</span
											>
											<span class="mr-2" v-if="substances"
												><strong>Substance: </strong>({{ substances }})</span
											>
											<span class="mr-2" v-if="products"
												><strong>Product: </strong>({{ products }})</span
											>

											<span class="mr-2" v-if="formulations"
												><strong>Formulation: </strong>({{ formulations }})</span
											>
											<span class="mr-2" v-if="strengths"
												><strong>Strength: </strong>({{ strengths }})</span
											>
											<span class="mr-2" v-if="salesChannel"
												><strong>Sales Channel: </strong>({{ salesChannel }})</span
											>

											<span class="mr-2" v-if="vnrs"
												><strong>Nordic Product no: </strong>({{ vnrs }})</span
											>
											<span class="mr-2" v-if="companies"
												><strong>Company: </strong>({{ companies }})</span
											>
											<span class="mr-2" v-if="measurements"
												><strong>Measurement: </strong> ({{ measurements }})</span
											>

											<span class="mr-2" v-if="periods"
												><strong>Period: </strong>({{ periods }})</span
											>
											<span class="mr-2" v-if="years"><strong>Year: </strong>({{ years }})</span>
											<span class="mr-2" v-if="months"
												><strong>Month: </strong>({{ months }})</span
											>
										</span>
									</v-expansion-panel-header>
									<v-expansion-panel-content>
										<SearchForm :item="item" @pivot-data="handlePivotData" />
									</v-expansion-panel-content>
								</v-expansion-panel>
							</v-expansion-panels>
							<PivotTable
								v-if="item.content.pivotData && item.content.pivotData.length"
								:tabId="item.tabId"
								:pivotData="item.content.pivotData"
								:pivotConfig="item.content.pivotConfig"
							/>
						</v-tab-item>
					</v-tabs-items>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
	import utils from '@/utils.js';
	import Echo from 'laravel-echo';
	import CryptoJS from 'crypto-js';
	import { v4 as uuidv4 } from 'uuid';
	import { eventBus } from '@/eventBus';
	import { PMX_DASHBOARD_CONFIG } from '@/config';
	import Tooltip from '@/components/Tooltip.vue';
	import Snackbar from '@/components/Snackbar.vue';
	import SearchForm from '@/components/SearchForm.vue';
	import PivotTable from '@/components/PivotTable.vue';
	import SavedSearch from '@/components/SavedSearch.vue';
	import OverlayCircular from '@/components/OverlayCircular.vue';

	window.Pusher = require('pusher-js');

	if (process.env.VUE_APP_NODE_ENV !== 'production') {
		Pusher.logToConsole = true;
	}

	/**
	 * @module SearchView
	 *
	 * @vue-data {boolean} [dialogIndex=false] - Open/close dialog for saved searches
	 * @vue-data {number} [tab=null] - Current opened tab
	 * @vue-data {number} [panel=0] - Controls the opened/closed state the expansion panel
     * 
	 * @vue-data {array<object>} [items=[]] - List of tabs
	 * @vue-data {string} [items.tabId=uuidv4()] - Generate ID for every new tab
	 * @vue-data {object} [items.content={}] - Contents of the tab
	 * @vue-data {string} [items.content.created_at=''] - Tab creation date
	 * @vue-data {string|number} [items.content.id=0] - Tab ID
	 * @vue-data {string} [items.content.search_name=''] - Tab name which is used for saved searches
	 * @vue-data {string} [items.content.search_params={}] - The search input parameters for corresponding tab
	 * @vue-data {boolean} [items.content.editing=false] - Tab is in edit mode
	 * @vue-data {boolean} [items.content.searching=false] - Tab is search mode
	 * @vue-data {array} [items.content.pivotData=[]] - Pivot table data
	 * @vue-data {object} [items.content.pivotConfig={}] - Pivot table configs
     * 
     * @vue-computed {string} tabName - Display tab name
     * @vue-computed {object} currentTab - Gets the current tab content 
     * @vue-computed {object} currentSearchParams - Gets the search parameters for the current tab 
     * @vue-computed {string} countries - Display countries for current tab in expansion panel header  
     * @vue-computed {string} regions - Display regions for current tab in expansion panel header  
     * @vue-computed {string} bricks - Display bricks for current tab in expansion panel header  
     * @vue-computed {string} products - Display products for current tab in expansion panel header  
     * @vue-computed {string} atcCodes - Display ATC Codes for current tab in expansion panel header  
     * @vue-computed {string} substances - Display substances for current tab in expansion panel header  
     * @vue-computed {string} formulations - Display formulations for current tab in expansion panel header  
     * @vue-computed {string} strengths - Display strengths for current tab in expansion panel header  
     * @vue-computed {string} salesChannel - Display sales channels for current tab in expansion panel header  
     * @vue-computed {string} vnrs - Display vnrs for current tab in expansion panel header  
     * @vue-computed {string} companies - Display companies for current tab in expansion panel header  
     * @vue-computed {string} measurements - Display measurements for current tab in expansion panel header  
     * @vue-computed {string} periods - Display periods for current tab in expansion panel header  
     * @vue-computed {string} years - Display years for current tab in expansion panel header  
     * @vue-computed {string} months - Display months for current tab in expansion panel header  
	 */

	export default {
		name: 'Search',
		components: {
			Tooltip,
			OverlayCircular,
			Snackbar,
			SearchForm,
			SavedSearch,
			PivotTable,
		},
		data: () => ({
			dialogIndex: false,
			tab: null,
			panel: 0,
			items: [
				{
					tabId: uuidv4(),
					content: {
						created_at: '',
						id: 0,
						search_name: '',
						search_params: {},
						editing: false,
						searching: false,
						pivotData: [],
						pivotConfig: {},
					},
				},
			],
		}),
		created() {
			// Get company ID and decrypt it
			const companyID = localStorage.getItem('companyID');
			const decrypted = CryptoJS.AES.decrypt(companyID, process.env.VUE_APP_GOOGLE_SITE_KEY).toString(
				CryptoJS.enc.Utf8
			);

			// Do  not initialize Pusher if company is Gilead
			if (decrypted !== '13') {
				// Get access token
				const accessToken = JSON.parse(localStorage.getItem('user')).access_token;

				window.echo = new Echo({
					broadcaster: 'pusher',
					key: process.env.VUE_APP_PUSHER_APP_KEY,
					cluster: process.env.VUE_APP_PUSHER_APP_CLUSTER,
					encrypted: true,
					authEndpoint: PMX_DASHBOARD_CONFIG.AUTH_ENDPOINT,
					auth: {
						headers: {
							Authorization: `Bearer ${accessToken}`,
						},
					},
				});

				// Bind on the private channel "brick_stats" and listen for "brickStatsComplete" event
				window.echo.private('bricks_stats').listen('.App\\Events\\bricksStatsComplete', e => {
					// Emit data to "SearchForm" component to corresponding tab ID
					eventBus.$emit('pusher-event-' + e.stats_tab, e);
				});
			}
		},

		/**
		 * Listen for value changes in items
		 *
		 * @property {function} items - Length of tab list  
		 */
		watch: {
			items(value) {
				this.tab = value.length - 1;
			},
		},
		computed: {
			searchFieldsIsEmpty() {
				return (
					!this.countries &&
					!this.regions &&
					!this.bricks &&
					!this.atcCodes &&
					!this.substances &&
					!this.products &&
					!this.formulations &&
					!this.strengths &&
					!this.salesChannel &&
					!this.vnrs &&
					!this.companies &&
					!this.measurements &&
					!this.periods &&
					!this.years &&
					!this.months
				);
			},
			tabName() {
				return this.items.map(item => {
					if (item.content.search_name.length <= 11) {
						return item.content.search_name;
					} else {
						return item.content.search_name.substring(0, 11) + '...';
					}
				});
			},
			currentTab() {
				if (this.items[this.tab] !== undefined) {
					return this.items[this.tab];
				}
			},
			currentSearchParams() {
				if (this.currentTab !== undefined) {
					return this.currentTab.content.search_params;
				}
			},
			countries() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.countries);
				}
			},
			regions() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.regions);
				}
			},
			bricks() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.bricks);
				}
			},
			products() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.products);
				}
			},
			atcCodes() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.atcCodes);
				}
			},
			substances() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.substances);
				}
			},
			formulations() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.formulations);
				}
			},
			strengths() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.strengths);
				}
			},
			salesChannel() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.salesChannel);
				}
			},
			vnrs() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.vnrs);
				}
			},
			companies() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.companies);
				}
			},
			measurements() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.measurements);
				}
			},
			periods() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.periods);
				}
			},
			years() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.years);
				}
			},
			months() {
				if (this.currentTab !== undefined) {
					return this.commaSeperate(this.currentSearchParams, this.currentSearchParams.months);
				}
			},
		},
		methods: {

			/**
			 * Separate each value with a comma
             * 
             * @param {object} obj - Search parameters
             * @param {array} arr - Search properties
			 *
			 * @returns {string} - String with comma between values
			 */
			commaSeperate(obj, arr) {
				if (!utils.isEmptyObject(obj)) {
					if (arr && arr.length) {
						if (utils.isObjectInArray(arr)) {
							return arr.map(el => el.text).join(', ');
						} else {
							return arr.join(', ');
						}
					}
				}
			},

            /**
             * Close saved searches modal 
             */
			handleCloseIndex() {
				this.dialogIndex = false;
			},

            /**
             * Open new tab with edit mode 
             * 
             * @param {object} value - The tab item 
             * 
             * @returns {void}
             */
			editSavedSearch(value) {
				if (value) {
					this.addTab(value);
				}
			},

            /**
             * Open new tab with the selected saved search parameters 
             * 
             * @param {object} value - The tab item 
             * 
             * @returns {void}
             */
			submitSavedSearch(value) {
				if (value) {
					this.addTab(value);
				}
			},

            /**
             * Pass pivot data payload to the tab with matching ID  
             * 
             * @param {object} payload - Pivot data 
             * 
             * @returns void 
             */
			handlePivotData(payload) {
				const { pivotData, searchParams, tabId } = payload;
				const tab = this.items.find(el => el.tabId === tabId);

                // Pivot table will not be displayed if there is no pivot data 
				if (!pivotData.length) {
					tab.content.pivotData = [];
				} else {
					tab.content.pivotData = pivotData;
					tab.content.search_params = searchParams;

                    // Hide expansion panel when pivot table is being displayed 
					this.$refs[tab.tabId][0].$el.click();
				}
			},

            /**
             * Add a tab 
             * 
             * @param {object} value - The saved search
             * 
             * @returns {void}
             */
			addTab(value) {
                // If we select a saved search 
				if (value && value.id !== 0) {
					this.items.push({
						tabId: uuidv4(),
						content: value,
					});
				} else {
					this.items.push({
						tabId: uuidv4(),
						content: {
							created_at: '',
							id: 0,
							search_name: '',
							search_params: {},
							editing: false,
							searching: false,
							pivotData: [],
							pivotConfig: {},
						},
					});
				}
			},

            /**
             * Close the selected tab 
             * 
             * @param {number} index - Tab index 
             * 
             * @returns {void}
             */
			closeTab(index) {
				this.items.splice(index, 1);
			},
		},
	};
</script>

<style>
	/* v-tabs-items */
	.v-window__container.v-window__container--is-active {
		height: 48px !important;
	}

	.v-tab--active {
		background: #eee;
		transition: 2s;
	}
</style>
